import { fromFieldChanges } from "components/change-history-panel/change-history-entry";
import { pageEntries } from "components/change-history-panel/change-history-panel";
import { HttpErrorResponse } from "@angular/common/http";
import { Component, OnDestroy } from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";
import { TranslateService } from "@ngx-translate/core";
import {
  Events,
  ModalController,
  NavController,
  PopoverController,
  ToastController,
} from "ionic-angular";
import SortOptions from "models/content-wrapper/sort-options";
import { Subscription } from "rxjs";
import { Payer } from "models/payers/payer";
import { Config } from "pages/patient-chart/patient-benefits/models";
import { PatientBenefitsService } from "providers/patient-charts/patient-benefits.service";
import { PayersService } from "providers/payers-service/payers.service";
import { StateManagementService } from "providers/state-management/state-management.service";
import { UtilityService } from "providers/utility-service/utility-service";
import { commonVar } from "providers/common-var/common";
import PhoneUtils from "utils/phone.utils";
import { CreatePayerPage } from "./create-payer/create-payer";

const PAYER = "PayerConfig";
const MEDICAID = "MEDICAID";

// CO-3898 (F1.S1). The Coverage Detail cell for a Medicaid payer that carries no
// State. CONFIRMED BY PRODUCT (Claudia Baker, CO-3898, 2026-09-10): "Option A -
// need state build sounds good." Proposed by Will Bunker the same day and confirmed
// as written, so this is a settled ruling rather than a provisional one. Still ONE
// named constant pinned by a test, so a future re-ruling stays a one-line swap.
//
// Why a named value rather than the "-" used for non-Medicaid payers: this cell is
// the only surface in the product where anyone can see whether a payer has been
// classified. Rendering "-" makes an unfinished Medi-Cal payer indistinguishable
// from Aetna and destroys the onboarding worklist. Production held 453 of 453
// Medicaid payers with no State (2026-09-04, CO-3866), and the plan address is a
// remit-to address rather than the programme state, so those records must be
// curated by a human - who needs to be able to see them.
export const COVERAGE_DETAIL_NEEDS_STATE = "Medicaid - Need state";

// The value shown for any payer the Coverage Detail column has nothing to say
// about - Commercial, Medicare, Self-Pay. BDD F1.S1 spells it as an em dash.
export const COVERAGE_DETAIL_NONE = "\u2014";
const ACTIVE = "Active";
const ARCHIVED = "Archived";
const DELETED = "Deleted";

@Component({
  selector: "payers-view",
  templateUrl: "payers-view.html",
})
export class PayersView implements OnDestroy {
  payers: Payer[] = [];
  sortOptions: SortOptions = new SortOptions("payerName", "asc");

  changesPageSize: number = 10;
  // CO-2315 item #11: the AUDIT pager, deliberately separate from changesPageSize above.
  // That field pages the payer LIST (payers-view.ts:108, :253); reusing it would couple two
  // unrelated pagers, so changing how many payers a page shows would silently change how
  // many audit rows a payer's history shows.
  auditPageSize: number = 10;
  currentPage: number = 0;
  pages: number[] = [];
  totalPages: number = 0;
  searchInput: string = "";
  payersSearchDetails: any = null;

  selectedBenefitType: any = null;
  selectedPayerType: any = null;

  // CO-3898 (F1.S1/F1.S2): the Medi-Cal Payer Foundation toggle. It governs ONLY
  // whether the Coverage Detail column and the State filter render. The derivation
  // itself (coverageDetail) is a pure function of the payer's own fields and does
  // not consult the toggle - so a late-arriving toggle cannot leave a stale value
  // on a row that was mapped before it flipped.
  californiaMediCalSupportToggle: boolean = false;
  private mediCalToggleSub: Subscription;

  // Option list for the State filter. Rendered only with the toggle on.
  stateItems: Config[] = commonVar.STATE_LIST.map((code) => ({
    name: commonVar.STATE_NAMES[code] || code,
    value: code,
    disabled: false,
  }));
  responseMsg: any;
  gridForm: FormGroup;

  payerTypeItems: Config[] = [
    { name: "Commercial", value: "COMMERCIAL", disabled: false },
    { name: "Medicaid", value: "MEDICAID", disabled: false },
    { name: "Medicare", value: "MEDICARE", disabled: false },
  ];

  benefitTypeItems: Config[] = [
    { name: "Hospice", value: "HOSPICE", disabled: false },
    { name: "Palliative", value: "PALLIATIVE", disabled: false },
    { name: "Room And Board", value: "ROOMANDBOARD", disabled: false },
  ];

  statusItems = [
    { name: "Active", value: ACTIVE },
    { name: "Archived", value: ARCHIVED },
  ];

  constructor(
    private toastCtrl: ToastController,
    private fb: FormBuilder,
    protected translate: TranslateService,
    private payersService: PayersService,
    protected navCtrl: NavController,
    protected modalCtrl: ModalController,
    private patientBenefitsService: PatientBenefitsService,
    private utilityService: UtilityService,
    protected popoverCtrl: PopoverController,
    protected events: Events,
    private stateManagementService: StateManagementService
  ) {
    this.gridForm = fb.group({
      payerType: null,
      benefitType: null,
      searchInput: null,
      status: null,
      // CO-3898: present in the form whatever the toggle says, so the control set
      // does not change shape mid-session; the TEMPLATE decides whether it renders
      // and createSearchRequestBody only reads it when the toggle is on.
      state: null,
    });
  }

  ngOnInit() {
    // CO-3898: sibling pattern from general-payer.ts - subscribe, coerce, keep the
    // handle so ngOnDestroy can release it.
    if (
      this.stateManagementService &&
      this.stateManagementService.californiaMediCalSupportToggle
    ) {
      this.mediCalToggleSub =
        this.stateManagementService.californiaMediCalSupportToggle.subscribe(
          (enabled) => {
            this.californiaMediCalSupportToggle = !!enabled;
          }
        );
    }
    this.getPayers();
  }

  ngOnDestroy() {
    if (this.mediCalToggleSub) {
      this.mediCalToggleSub.unsubscribe();
      this.mediCalToggleSub = null;
    }
  }

  /**
   * CO-3898 (BDD F1.S1) - the Coverage Detail cell.
   *
   * THE DISCRIMINATOR IS `state`, NOT `isManagedCare`. `IsManagedCare` is a bool
   * marshalled `omitempty` on the backend (payer_config_dto.go), so a Fee-for-Service
   * payer's OpenSearch document carries NO `isManagedCare` key at all - not `false`.
   * A never-classified Medicaid payer looks identical on that field, and so does
   * Aetna. Keying FFS off `isManagedCare === false` would collapse all three.
   * `payer_classification_roundtrip_test.go` pins the absence.
   *
   * Round-2 register item #6 (Product, 2026-08-28) settles the other half: "the
   * Fee-for-Service default COUNTS AS SET, so Payer Type alone classifies a Medicaid
   * payer". Coverage Type is therefore never the missing half - State is.
   *
   * Case-insensitive on payerType deliberately: mapPayersResponse pascal-cases it to
   * "Medicaid" for display, while a raw DTO carries "MEDICAID".
   */
  coverageDetail(payer): string {
    if (!payer) return COVERAGE_DETAIL_NONE;

    const payerType = (payer.payerType || "").toString().trim();
    if (payerType.toUpperCase() !== MEDICAID) return COVERAGE_DETAIL_NONE;

    const stateCode = (payer.state || "").toString().trim().toUpperCase();
    if (!stateCode) return COVERAGE_DETAIL_NEEDS_STATE;

    const stateName = commonVar.STATE_NAMES[stateCode] || stateCode;
    return payer.isManagedCare === true
      ? stateName + " MCP"
      : stateName + " FFS";
  }

  /**
   * CO-3898 (F15.S2): the cell is announced with its row's payer name, so a screen
   * reader user hears which payer a "California MCP" belongs to.
   */
  coverageDetailLabel(payer): string {
    const name = (payer && payer.payerName) || "";
    return name
      ? name + ", Coverage Detail: " + this.coverageDetail(payer)
      : "Coverage Detail: " + this.coverageDetail(payer);
  }

  applyFiltersOS() {
    this.currentPage = 0;
    this.getPayers();
  }

  getPayers() {
    this.payers = [];
    this.createSearchRequestBody();
    this.responseMsg = "Loading payers...";

    this.payersService.searchPayersFromOS(this.payersSearchDetails).subscribe(
      (response) => {
        if (
          response !== null &&
          response["hits"] != null &&
          response["hits"].length > 0
        ) {
          this.mapPayersResponse(response["hits"]);
          this.totalPages = Math.ceil(
            response["totalHits"] / this.changesPageSize
          );
        } else {
          this.payers = [];
          this.responseMsg = "No Payer Found";
        }
      },
      (err) => {
        console.error("Error occurred:", err);
        console.error("Error details:", {
          status: err.status,
          statusText: err.statusText,
          message: err.message,
          error: err.error,
        });

        this.presentErrorToast(
          err,
          this.translate.instant("Failed to retrieve payers.")
        );
        this.responseMsg =
          "Failed to retrieve payers: Status " +
          JSON.stringify(err.status) +
          " : " +
          JSON.stringify(err.statusText);
      }
    );
  }

  createSearchRequestBody() {
    let details: any = {};
    details.dataType = PAYER;
    details.mustFields = [
      {
        name: "dataType",
        value: PAYER,
      },
    ];

    // Process payerType filter
    if (
      this.gridForm &&
      this.gridForm.controls["payerType"].value &&
      this.gridForm.controls["payerType"].value != ""
    ) {
      const payerTypeValue =
        this.gridForm.controls["payerType"].value.toString();
      details.mustFields.push({
        name: "payerType",
        value: payerTypeValue,
      });
    }

    // Process benefitType filter
    if (
      this.gridForm &&
      this.gridForm.controls["benefitType"].value &&
      this.gridForm.controls["benefitType"].value != ""
    ) {
      const benefitTypeValue =
        this.gridForm.controls["benefitType"].value.toString();
      details.mustFields.push({
        name: "benefitType",
        value: benefitTypeValue,
      });
    }

    // CO-3898 (F1.S3): the State filter. Read ONLY when the Medi-Cal Payer
    // Foundation is enabled - with the toggle off the control does not render, so
    // emitting the field would change the query for a tenant whose screen is
    // supposed to be byte-for-byte unchanged (A.1.6/A.1.8).
    if (
      this.californiaMediCalSupportToggle &&
      this.gridForm &&
      this.gridForm.controls["state"] &&
      this.gridForm.controls["state"].value &&
      this.gridForm.controls["state"].value != ""
    ) {
      details.mustFields.push({
        name: "state",
        value: this.gridForm.controls["state"].value.toString(),
      });
    }

    if (
      this.gridForm &&
      this.gridForm.controls["status"].value &&
      this.gridForm.controls["status"].value != ""
    ) {
      const statusValue = this.gridForm.controls["status"].value;

      if (statusValue == ACTIVE) {
        details.mustNotFields = [
          {
            name: "status",
            value: DELETED.toUpperCase(),
          },
          {
            name: "status",
            value: ARCHIVED.toUpperCase(),
          },
        ];
      }

      if (statusValue == ARCHIVED) {
        details.mustFields.push({
          name: "status",
          value: ARCHIVED.toUpperCase(),
        });
      }
    } else {
      details.mustNotFields = [
        {
          name: "status",
          value: DELETED.toUpperCase(),
        },
      ];
    }

    if (this.searchInput) {
      const searchTerm = this.searchInput.trim();
      const normalizedTerm = searchTerm.toLowerCase();

      // Detect if user is searching for status values (partial match, minimum 2 chars)
      const isSearchingActive =
        normalizedTerm.length >= 2 && "active".startsWith(normalizedTerm);
      const isSearchingArchived =
        normalizedTerm.length >= 2 && "archived".startsWith(normalizedTerm);

      if (isSearchingActive) {
        // User is searching for "Active" - convert to filter (exclude DELETED and ARCHIVED)
        details.mustNotFields = [
          { name: "status", value: DELETED.toUpperCase() },
          { name: "status", value: ARCHIVED.toUpperCase() },
        ];
        details.SearchString = null;
      } else if (isSearchingArchived) {
        // User is searching for "Archived" - convert to filter (require ARCHIVED status)
        details.mustFields.push({
          name: "status",
          value: ARCHIVED.toUpperCase(),
        });
        // Clear mustNotFields since we want archived records
        details.mustNotFields = [];
        details.SearchString = null;
      } else {
        // Normal text search - apply phone number normalization
        const normalizedSearchInput =
          PhoneUtils.normalizeSearchInputForPhoneNumbers(this.searchInput);
        details.SearchString = normalizedSearchInput;
      }
    } else {
      details.SearchString = null;
    }

    // Set remaining fields
    details.dateRange = null;
    details.OptionalFields = [];
    details.sortBy = {
      name: this.sortOptions.column,
      value: this.sortOptions.direction.toLowerCase(),
    };
    details.From = this.currentPage * this.changesPageSize;
    details.Size = this.changesPageSize;

    this.payersSearchDetails = details;
  }

  getPayersByPage(pageNumber) {
    this.currentPage = pageNumber;
    this.getPayers();
  }

  sortBy(selectedCol) {
    if (this.sortOptions.column === selectedCol) {
      const newDirection =
        this.sortOptions.direction === "asc" ? "desc" : "asc";
      this.sortOptions.direction = newDirection;
    } else {
      this.sortOptions.column = selectedCol;
      this.sortOptions.direction = "asc";
    }
    this.getPayers();
  }

  newPayer() {
    this.navCtrl.push(CreatePayerPage, {
      parent: this,
    });
  }

  editPayer(payer) {
    this.navCtrl.push(CreatePayerPage, {
      parent: this,
      payerData: payer,
    });
  }

  runReport() {
    this.events.publish("runPayerReport");
  }

  arrowClick(payer) {
    payer.displayDetails = !payer.displayDetails;
    if (payer.displayDetails) {
      this.addChangesDetails(payer);
    }
  }

  // CO-2315 item #11 / F2.S13. Pages the payer's change audit for <change-history-panel>.
  //
  // Deliberately the same shape as facilities-view.addChangesDetails, because the two
  // screens now render the same component and a reader moving between them should not meet
  // two different paging behaviours. Built on expand rather than on load: a tenant's payer
  // list is long and most rows are never opened.
  addChangesDetails(payer) {
    payer.currentChangesPage = 0;
    payer.changesPageCount = 0;
    payer.pagedChanges = [];

    const entries = fromFieldChanges(payer.payerChanges || []);
    if (!entries.length) {
      return;
    }

    payer.pagedChanges = pageEntries(entries, this.auditPageSize);
    payer.changesPageCount = payer.pagedChanges.length;
  }

  changesOnPageChange(event: number, payer) {
    payer.currentChangesPage = event;
  }

  // Cached per payer - a fresh arrow each change-detection pass would hand the panel a new
  // @Input identity every tick.
  changesOnPageChangeFor(payer): (page: number) => void {
    if (!payer.changesPageChangedHandler) {
      payer.changesPageChangedHandler = (page: number) =>
        this.changesOnPageChange(page, payer);
    }
    return payer.changesPageChangedHandler;
  }

  clearSearch() {
    this.gridForm.controls["benefitType"].setValue(null);
    this.gridForm.controls["payerType"].setValue(null);
    this.gridForm.controls["status"].setValue(null);
    // CO-3898: cleared unconditionally. The control exists whatever the toggle
    // says, so a stale value must not survive a Clear Filters.
    this.gridForm.controls["state"].setValue(null);
    this.searchInput = null;
    this.currentPage = 0;

    this.getPayers();
  }

  mapPayersResponse(payersResponse) {
    this.payers = [];
    payersResponse.forEach((payerDto, index) => {
      let planAddress =
        payerDto.plans && payerDto.plans.length > 0
          ? payerDto.plans.filter((x) => x.planName == "PlanAddress")[0]
          : null;
      let payer: Payer = {
        authorizationRules: payerDto.authorizationRules,
        benefitType: payerDto.benefitType
          ? this.formatBenefitType(payerDto.benefitType)
          : "-",
        claimRules: payerDto.claimRules,
        complianceRules: payerDto.complianceRules,
        dataType: payerDto.dataType,
        isActive: payerDto.isActive,
        osUpdated: payerDto.osUpdated,
        payerChanges: payerDto.payerChanges,
        payerId: payerDto.payerId,
        payerName: payerDto.payerName,
        payerSubtype: payerDto.payerSubtype,
        payerType: payerDto.payerType
          ? this.toPascalCase(payerDto.payerType)
          : "-",
        plans: payerDto.plans,
        rliRules: payerDto.payerSubtype,
        tenantId: payerDto.tenantId,
        updated: payerDto.updated,
        displayDetails: false,
        payerAddress: this.getPayerAddress(payerDto),
        contactNumber: payerDto.contactNumber,
        contactNumberToDisplay: payerDto.contactNumber
          ? this.formatPhoneNumber(payerDto.contactNumber)
          : "-",
        notes: planAddress && planAddress.notes ? planAddress.notes : "-",
        payToAddress: payerDto.payToAddress ? payerDto.payToAddress : "-",
        webAddress:
          planAddress && planAddress.webAddress ? planAddress.webAddress : "-",
        useSameAddress: payerDto.useSameAddress
          ? payerDto.useSameAddress
          : false,
        scheduleEligibility: payerDto.scheduleEligibility,
        instantEligibilityClearinghouseId:
          payerDto.instantEligibilityClearinghouseId,
        scheduleFrequency: payerDto.scheduleFrequency,
        scheduleFrequencyUnit: payerDto.scheduleFrequencyUnit,
        scheduleUpdatedDate: payerDto.scheduleUpdatedDate,
        scheduleReceiverId: payerDto.scheduleReceiverId,
        instantReceiverId: payerDto.instantReceiverId,
        status: payerDto.status
          ? payerDto.status.toLowerCase() == "archived"
            ? ARCHIVED
            : ACTIVE
          : ACTIVE,
        // CO-3610: carry the state-Medicaid classification fields through to
        // the edit payer object (handed to CreatePayer via navParams). Without
        // these, the General tab reopened with an empty Medicaid State and an
        // empty Coverage Type even though the backend persisted them.
        state: payerDto.state,
        isManagedCare: payerDto.isManagedCare,
        isMedicareAdvantage: payerDto.isMedicareAdvantage,
        parentStateMedicaidPayerId: payerDto.parentStateMedicaidPayerId,
        hospiceCarveOutToFfs: payerDto.hospiceCarveOutToFfs,
      };
      this.payers.push(payer);
    });
  }

  getPayerAddress(payerDto): string {
    if (payerDto.payerAddress && payerDto.payerAddress != "")
      return payerDto.payerAddress;
    else if (
      payerDto.plans &&
      (payerDto.plans.length == 1 || payerDto.plans.length > 2)
    ) {
      return this.formatAddressToShow(payerDto.plans[0]);
    } else if (payerDto.plans && payerDto.plans.length == 2) {
      let plan = payerDto.plans.filter((x) => x.planName == "PayAddress")[0];
      if (!plan) plan = payerDto.plans[0];
      if (plan.address1 == "") return "-";
      return this.formatAddressToShow(plan);
    }
    return "-";
  }

  formatAddressToShow(plan) {
    const addressComponents = [
      plan.address1 && plan.address1.trim(),
      plan.city && plan.city.trim(),
      plan.state && plan.state.trim(),
      plan.zipCode && plan.zipCode.trim(),
      plan.zipCodeExtension && plan.zipCodeExtension.trim(),
    ];

    return addressComponents
      .filter(Boolean)
      .join(", ")
      .replace(/, (\w+)/g, " $1");
  }

  presentErrorToast(err: HttpErrorResponse, message: string) {
    if (err && (err as any).code === "REFRESH_TOKEN_FAILED") {
      return;
    }
    message += " ";
    if (err.error instanceof Error) {
      message += "Client error.";
    } else {
      message += " ";
      if (err.status == 401) {
        message += "User not authorized.";
      } else if (err.status >= 500) {
        // Do not modify message
      } else {
        message += "Communication error.";
      }
    }

    let toast = this.toastCtrl.create({
      message: message,
      duration: 6000,
      position: "below",
      cssClass: "error",
    });

    toast.present();
  }

  toPascalCase(str) {
    return str
      .toLowerCase()
      .replace(/(?:^|[^a-zA-Z0-9]+)(.)/g, (match, char) => char.toUpperCase());
  }

  formatBenefitType(str) {
    return str
      .split(",")
      .map((word) =>
        word.trim().toLowerCase() === "roomandboard" ||
        word.trim().toLowerCase() === "room and board"
          ? "Room And Board"
          : word.trim().charAt(0).toUpperCase() +
            word.trim().slice(1).toLowerCase()
      )
      .join(",");
  }

  formatPhoneNumber(phoneNumber) {
    if (phoneNumber.length === 10 && /^\d{10}$/.test(phoneNumber)) {
      const areaCode = phoneNumber.slice(0, 3);
      const firstPart = phoneNumber.slice(3, 6);
      const secondPart = phoneNumber.slice(6);
      phoneNumber = `(${areaCode})${firstPart}-${secondPart}`;
    }
    return phoneNumber;
  }

  payerOptions(ev, payer) {
    const options = this.popoverCtrl.create("PayerViewOptionsPage", payer, {
      cssClass: "payer-view-options-popover",
    });
    options.onDidDismiss((data) => {
      if (data) {
        if (data === "delete") {
          this.openDeleteModal(payer);
        } else if (data === "edit") {
          this.navCtrl.push(CreatePayerPage, {
            parent: this,
            payerData: payer,
          });
        } else if (data === "archive") {
          this.openArchiveModal(payer);
        }
      }
    });
    options.present({ ev });
  }

  openArchiveModal(payer) {
    let archiveModal = this.modalCtrl.create(
      "PayerViewArchiveModal",
      {
        payerId: payer.payerId,
      },
      { enableBackdropDismiss: true, cssClass: "payer-view-archive-modal" }
    );

    archiveModal.onDidDismiss((data) => {
      if (data) {
        delete data.dismiss;
        let value = this.bulidPayerPayload(payer, ARCHIVED.toUpperCase());
        return this.payersService
          .savePayerDetails(value)
          .pipe()
          .take(1)
          .subscribe(
            (o) => {
              setTimeout(() => this.getPayers(), 2000);
            },
            (x) => console.error("Error")
          );
      }
    });

    archiveModal.present();
  }

  openDeleteModal(payer) {
    let deleteModal = this.modalCtrl.create(
      "PayerViewDeleteModal",
      {
        payerId: payer.payerId,
      },
      { enableBackdropDismiss: true, cssClass: "payer-view-delete-modal" }
    );

    deleteModal.onDidDismiss((data) => {
      if (data) {
        delete data.dismiss;
        this.utilityService.increaseSpinnerCount("Delete Payer...");
        this.patientBenefitsService
          .canDeletePayer(payer.payerId)
          .pipe()
          .take(1)
          .subscribe((canDelete: boolean) => {
            if (canDelete) {
              this.deletePayer(payer);
            } else {
              const msg = `This payer ${payer.payerName} cannot be deleted because it is currently assigned to a patient in Benefits.`;
              this.utilityService.presentCustomErrorToast(msg);
            }
            this.utilityService.decreaseSpinnerCount();
          });
      }
    });
    deleteModal.present();
  }

  deletePayer(payer) {
    let value = this.bulidPayerPayload(payer, DELETED.toUpperCase());
    return this.payersService
      .savePayerDetails(value)
      .pipe()
      .take(1)
      .subscribe(
        (o) => {
          setTimeout(() => this.getPayers(), 2000);
        },
        (x) => console.error("Error")
      );
  }

  bulidPayerPayload(payer, status: string) {
    return {
      payerId: payer.payerId,
      tenantId: payer.tenantId,
      payerName: payer.payerName,
      payerType: payer.payerType.toUpperCase(),
      benefitType: payer.benefitType,
      plans: payer.plans,
      payerAddress: payer.payerAddress,
      contactNumber: payer.contactNumber,
      useSameAddress: payer.useSameAddress,
      payToAddress: payer.payToAddress,
      scheduleEligibility: payer.scheduleEligibility,
      scheduleFrequency: payer.scheduleFrequency,
      scheduleReceiverId: payer.scheduleReceiverId,
      scheduleFrequencyUnit: payer.scheduleFrequencyUnit,
      scheduleUpdatedDate: payer.scheduleUpdatedDate,
      instantReceiverId: payer.instantReceiverId,
      instantEligibilityClearinghouseId:
        payer.instantEligibilityClearinghouseId,
      isActive: true,
      status: status,
    };
  }

  onActivate(payer) {
    let value = {
      payerId: payer.payerId,
      tenantId: payer.tenantId,
      payerName: payer.payerName,
      payerType: payer.payerType.toUpperCase(),
      benefitType: payer.benefitType,
      plans: payer.plans,
      payerAddress: payer.payerAddress,
      contactNumber: payer.contactNumber,
      useSameAddress: payer.useSameAddress,
      payToAddress: payer.payToAddress,
      scheduleEligibility: payer.scheduleEligibility,
      scheduleFrequency: payer.scheduleFrequency,
      scheduleReceiverId: payer.scheduleReceiverId,
      scheduleFrequencyUnit: payer.scheduleFrequencyUnit,
      scheduleUpdatedDate: payer.scheduleUpdatedDate,
      instantReceiverId: payer.instantReceiverId,
      instantEligibilityClearinghouseId:
        payer.instantEligibilityClearinghouseId,
      isActive: true,
      status: "",
    };
    return this.payersService
      .savePayerDetails(value)
      .pipe()
      .take(1)
      .subscribe(
        (o) => {
          setTimeout(() => this.getPayers(), 2000);
        },
        (x) => console.error("Error")
      );
  }
}



// WEBPACK FOOTER //
// ./src/pages/company/payers-view/payers-view.ts